import { useEffect } from "react";
import { useEditor } from "./useEditor";

/** Log resource that are large or take long to load */
export function usePerformanceMonitoring() {
	const { logger } = useEditor();

	const maxDurationInMs = 1000;
	const maxSizeInBytes = 3 * 1024 * 1024;

	useEffect(() => {
		// https://web.dev/custom-metrics/#resource-timing-api
		// Catch errors since some browsers throw when using the new `type` option.
		// https://bugs.webkit.org/show_bug.cgi?id=209216
		let po: PerformanceObserver | undefined;
		try {
			po = new PerformanceObserver((list) => {
				for (const entry of list.getEntries() as PerformanceResourceTiming[]) {
					// Skip checks on our javascript sizes
					if (entry.name.endsWith(".js")) return;

					// Show we log to GA or logviewer
					if (entry.transferSize > maxSizeInBytes) {
						logger.warn(
							`Performance warning: ${entry.name}: ${
								Math.round(entry.transferSize / 1024 / 102.4) / 10
							}MB`,
						);
					} else if (entry.duration > maxDurationInMs) {
						const size =
							entry.transferSize === 0
								? ", from cache"
								: `, ${Math.ceil(entry.transferSize / 1024)}KB`;
						const initiatorType = entry.initiatorType
							? `, initiatorType: ${entry.initiatorType}`
							: "";

						logger.warn(
							`Performance warning: ${entry.name}: Took ${Math.ceil(
								entry.duration,
							)}ms to load${size}${initiatorType}`,
						);
					}
				}
			});
			po.observe({ type: "resource", buffered: true });
		} catch (_e) {
			// Do nothing if the browser doesn't support this API.
		}

		return () => {
			po?.disconnect();
		};
	}, [logger, maxDurationInMs, maxSizeInBytes]);
}
